import React, { useEffect, useRef, useState } from "react";

import { useHistory } from "react-router";
import { useParams } from "react-router-dom";

import { CircularProgress, Grid } from "@material-ui/core";

import {
  downloadLabels,
  getShipments,
  partialShip,
  reprocessShipment,
} from "api/shipment";

import { Button } from "components/Form";
import PageTitle from "components/PageTitle";

import { useUserState } from "context/UserContext";

import { Printer } from "icons";

import { initialAPIListValue } from "utils/constants";

import parseJwt from "utils/jwt";
import { getWithExpiry } from "utils/storage";

import {
  ActionsButton,
  BackorderShipmentsDialog,
  PartialShipDialog,
  RequestCancellationDialog,
  RevertShipmentDialog,
  ShipmentCancelModal,
  ShipTheShipmentDialog,
} from "./ShipmentActions";
import ShipmentPreview from "./ShipmentPreview";
import { popupNotification, useLayoutDispatch } from "context/LayoutContext";

const REPRINTABLE_STATUSES = [
  "Confirmed",
  "Printed",
  "Picked",
  "Partial_Shipped",
  "Shipped",
  "Invoiced",
];

export default function ShipmentDetail() {
  const { shipment_id } = useParams();

  const history = useHistory();
  const { permissions } = useUserState();

  const [shipment, setShipment] = useState(initialAPIListValue);
  const [cancelShipmentID, setCancelShipmentID] = useState(false);
  const [cancelRequestedShipmentID, setCancelRequestedShipmentID] = useState(
    false,
  );
  const [cancellationOption, setCancellationOption] = useState("");
  const [revertShipmentID, setRevertShipmentID] = useState(false);
  const [hasShippedItem, setHasShippedItem] = useState(false);
  const hasCancelPermission = permissions?.includes(
    "contenttypes.shipment.cancel",
  );
  const [shipShipmentNumber, setShipShipmentNumber] = useState("");
  const [partialShipID, setPartialShipID] = useState("");
  const [backorderShipmentID, setBackorderShipmentID] = useState("");
  const [detailCount, setDetailCount] = useState(0);
  const [isReprinting, setIsReprinting] = useState(false);
  const reprintIntervalRef = useRef(null);
  const { seller_id } = parseJwt(getWithExpiry("access"));
  // Same rule the shipments list uses: backorder only has open lines to split off while the
  // shipment sits on Partial_Shipped, and it is an internal warehouse action.
  const canBackorder =
    seller_id === 1 && shipment.status === "Partial_Shipped";

  const layoutDispatch = useLayoutDispatch();

  useEffect(() => {
    getShipments({
      id: shipment_id,
      responseSetter: (data) => setShipment({ isFetching: false, ...data }),
    });
  }, [shipment_id]);

  useEffect(() => {
    return () => clearInterval(reprintIntervalRef.current);
  }, []);

  const handleReprintClick = () => {
    setIsReprinting(true);
    downloadLabels({
      params: {
        shipment_numbers: shipment.shipment_number,
        reprint: true,
      },
      responseSetter: (res) => {
        if (!res?.process_key) {
          setIsReprinting(false);
          return;
        }
        reprintIntervalRef.current = setInterval(() => {
          downloadLabels({
            params: { process_key: res.process_key },
            responseSetter: (res) => {
              if (res.success) {
                window.open(res.url, "_blank", "noopener,noreferrer");
                setIsReprinting(false);
                clearInterval(reprintIntervalRef.current);
              }
            },
          });
        }, 5000);
      },
    });
  };

  return shipment.isFetching ? (
    <CircularProgress />
  ) : (
    <>
      <ShipmentCancelModal
        shipment_id={cancelShipmentID}
        close={() => setCancelShipmentID(false)}
        afterCancelled={() => history.push("/app/shipments")}
      />
      <RequestCancellationDialog
        shipment_id={cancelRequestedShipmentID}
        close={() => setCancelRequestedShipmentID(false)}
        afterCancelled={() => history.push("/app/shipments")}
        option={cancellationOption}
      />
      <ShipTheShipmentDialog
        shipment_number={shipShipmentNumber}
        close={(status = false) => {
          if (status) {
            window.location.reload();
          }
          setShipShipmentNumber("");
        }}
      />
      <RevertShipmentDialog
        shipment_id={revertShipmentID}
        close={(status = false) => {
          if (status) {
            window.location.reload();
          }
          setRevertShipmentID("");
        }}
      />
      <PartialShipDialog
        shipment_id={partialShipID}
        close={(status = false) => {
          if (status) {
            window.location.reload();
          }
          setPartialShipID("");
        }}
      />
      <BackorderShipmentsDialog
        shipment_id={backorderShipmentID}
        close={(status = false) => {
          if (status) {
            window.location.reload();
          }
          setBackorderShipmentID("");
        }}
      />
      <PageTitle
        title={shipment.shipment_number}
        backLink="/app/shipments"
        button={
          (REPRINTABLE_STATUSES.includes(shipment.status) ||
            hasCancelPermission) && (
            <Grid container spacing={1}>
              {REPRINTABLE_STATUSES.includes(shipment.status) && (
                <Grid item>
                  <Button
                    color="blue"
                    disabled={isReprinting}
                    onClick={handleReprintClick}
                  >
                    {isReprinting ? (
                      <CircularProgress size={16} />
                    ) : (
                      <>
                        <Printer color="white" size={0.8} />
                        <span style={{ marginLeft: "0.2rem" }}>Reprint</span>
                      </>
                    )}
                  </Button>
                </Grid>
              )}
              {(hasCancelPermission || canBackorder) && (
                <Grid item>
                  <ActionsButton
                    style={{ marginRight: "10px" }}
                    status={shipment.status}
                    onCancelRequestClick={
                      hasCancelPermission
                        ? (option) => {
                            setCancelRequestedShipmentID(shipment_id);
                            setCancellationOption(option);
                          }
                        : null
                    }
                    onCancelClick={() => setCancelShipmentID(shipment_id)}
                    onShipClick={() =>
                      setShipShipmentNumber(shipment.shipment_number)
                    }
                    onReprocessClick={() => {
                      reprocessShipment({
                        id: shipment_id,
                        responseSetter: (res) => {
                          if (!res?.success && res?.message) {
                            popupNotification({
                              dispatch: layoutDispatch,
                              message: res.message,
                              status: "error",
                            });
                          } else if (res?.success) {
                            popupNotification({
                              dispatch: layoutDispatch,
                              message: "Shipment reprocessed successfully.",
                              status: "success",
                            });
                            setTimeout(() => {
                              window.location.reload();
                            }, 1000);
                          }
                        },
                      });
                    }}
                    onRevertClick={() => setRevertShipmentID(shipment_id)}
                    onPartialShipClick={detailCount > 1 ? () => setPartialShipID(shipment_id) : null}
                    onBackorderClick={
                      canBackorder
                        ? () => setBackorderShipmentID(shipment_id)
                        : null
                    }
                    hasShippedItem={hasShippedItem}
                  />
                </Grid>
              )}
            </Grid>
          )
        }
        multipleButtons={true}
        useHistoryBack
      />
      <ShipmentPreview
        shipment={shipment}
        setHasShippedItem={setHasShippedItem}
        setDetailCount={setDetailCount}
      />
    </>
  );
}
